<script lang="ts" setup>
import { ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { DBtn, DTextField, DTable, DLoading } from 'front-library'
import { useI18n } from 'vue-i18n'
import {
  getJoiningReasons,
  JoiningReasons
} from '@/app/core/services/joining-reasons'
import { useUserStore } from '@/store/user'
import { useRouter } from 'vue-router'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)

/* Datos */
const search = ref()
const tableJoiningReasons = ref(null)

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('joiningReasons.list.code'), table: 'code' },
  { name: 'name', title: t('joiningReasons.list.name'), table: 'name' },
  {
    name: 'updated_at',
    title: t('joiningReasons.list.lastUpdate'),
    table: 'updated_at'
  }
])
const rows = ref<JoiningReasons[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createJoiningReason = () => {
  void router.push({ name: 'CreateJoiningReason' })
}
const updateJoiningReason = (id: any) => {
  void router.push({ name: 'UpdateJoiningReason', params: { id } })
}
const getJoiningReason = (id: any) => {
  void router.push({ name: 'GetJoiningReason', params: { id } })
}

/* Listar motivos de ingreso */
const joiningReasons = async () => {
  loading.value = true
  try {
    const data: any = await getJoiningReasons(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('joiningReasons.list.title')" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('joiningReasons.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="joiningReasons" />
          <DBtn
            @click.prevent="joiningReasons"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          v-if="store.hasPermission('joining_reason:create')"
          :icon="'PlusIcon'"
          type="button"
          @click="createJoiningReason()">
          {{
            t('form.button.create', { form: t('joiningReasons.list.title', 0) })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableJoiningReasons"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), joiningReasons()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), joiningReasons()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('joining_reason:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getJoiningReason(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive && store.hasPermission('joining_reason:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateJoiningReason(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
